import { useEffect, useState } from "react";
import axios from "axios";
import ServiceTile from "@/components/ServiceTile";
import SEO, { SITE_URL } from "@/components/SEO";
import { API } from "@/lib/apiBase";

// Display order — Architects & Industry Professionals sits next to Heritage.
const SERVICE_ORDER = [
  "residential",
  "commercial",
  "rural",
  "heritage",
  "other-services",
  "subdivision",
  "terraced-housing",
  "coastal",
  "feasibility",
  "crosslease-to-freehold",
  "childcare-healthcare",
];

export default function ServicesPage() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API}/services`);
        const ordered = SERVICE_ORDER
          .map((slug) => response.data.find((s) => s.slug === slug))
          .filter(Boolean);
        setServices(ordered);
      } catch (error) {
        console.error("Error fetching services:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
  }, []);

  // Bento (no empty cells): Row1[Res2x2|Com|Rural] Row2[Res|Heritage|Architects] Row3[Subdiv2x|Terr|Coastal] Row4[Feas2x|Cross|Child]
  const bentoClasses = [
    "col-span-1 md:col-span-2 row-span-2 min-h-[400px]",
    "col-span-1 min-h-[200px]",
    "col-span-1 min-h-[200px]",
    "col-span-1 min-h-[200px]",
    "col-span-1 min-h-[200px]",
    "col-span-1 md:col-span-2 min-h-[250px]",
    "col-span-1 min-h-[200px]",
    "col-span-1 min-h-[200px]",
    "col-span-1 md:col-span-2 min-h-[250px]",
    "col-span-1 min-h-[200px]",
    "col-span-1 min-h-[200px]",
  ];

  return (
    <div className="page-wrapper bg-ink" data-testid="services-page">
      <SEO
        title="Planning Services Auckland — Resource Consents Across All Sectors"
        description="Resource consent and planning services across Auckland — residential, subdivision, commercial, rural, heritage, terraced housing, coastal, feasibility, cross lease conversion, childcare & healthcare. Senior-led Auckland planners."
        path="/services"
        keywords="planning services auckland, resource consent auckland, planner auckland, subdivision consent auckland, heritage consent auckland, terraced housing consent, town planner auckland"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Planning Services — Code Planning Limited",
          url: `${SITE_URL}/services`,
          description: "Our full range of resource consent and planning services across Auckland.",
          isPartOf: { "@id": `${SITE_URL}/#website` },
          mainEntity: {
            "@type": "ItemList",
            itemListElement: services.map((s, idx) => ({
              "@type": "ListItem",
              position: idx + 1,
              url: `${SITE_URL}/services/${s.slug}`,
              name: s.title,
            })),
          },
        }}
      />
      {/* Header */}
      <section className="py-12 md:py-16 bg-neutral-900">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6">
            Resource Consents<span className="italic text-gray-400">. Simplified.</span>
          </h1>
          <p className="text-lg md:text-xl text-gray-400 max-w-2xl leading-relaxed">
            From residential alterations to complex commercial developments, 
            we provide expert resource consent services across all sectors.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-10 md:py-14 bg-ink">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {[...Array(11)].map((_, i) => (
                <div
                  key={i}
                  className={`bg-neutral-800 animate-pulse ${bentoClasses[i] || "min-h-[200px]"}`}
                />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {services.map((service, index) => (
                <ServiceTile
                  key={service.id}
                  service={service}
                  className={bentoClasses[index] || "col-span-1 min-h-[200px]"}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Process Section */}
      <section className="py-12 md:py-16 bg-neutral-900">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-8 md:mb-10">
            <p className="text-sm uppercase tracking-widest text-gray-400 mb-4">
              How We Work
            </p>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold text-white">
              Our Process
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              {
                step: "01",
                title: "Initial Consultation",
                description:
                  "We review your project, assess planning constraints, and outline the consent pathway.",
              },
              {
                step: "02",
                title: "Application Preparation",
                description:
                  "We prepare comprehensive documentation addressing all relevant planning provisions. We also assemble a project team of like-minded professionals — including designers, engineers, and other professional services — and tailor that team to the needs and scale of your project.",
              },
              {
                step: "03",
                title: "Council Liaison",
                description:
                  "We manage all council communications and respond to any requests for information.",
              },
              {
                step: "04",
                title: "Consent Granted",
                description:
                  "Once approved, we ensure you understand all conditions and next steps.",
              },
            ].map((item, index) => (
              <div key={index} className="relative">
                <span className="font-heading text-6xl font-bold text-neutral-700 mb-4 block">
                  {item.step}
                </span>
                <h3 className="font-heading text-xl font-semibold text-white mb-3">
                  {item.title}
                </h3>
                <p className="text-gray-400 leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
